import { useEffect, useState, useCallback } from "react";
import {
  Search, MapPin, Users, Clock, Loader2, CircleDot, CheckCircle2, XCircle,
  CalendarClock, DollarSign, Phone, Route, ChevronRight, UserRound, TrendingUp, MessageSquare, Ban,
} from "lucide-react";
import { adminApi } from "./api";
import { PageHeader } from "./shared";
import { money } from "./userCells";
import { TripMessagesDialog } from "./FinanceDialogs";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const TABS = [
  { key: "upcoming", label: "Upcoming", icon: CalendarClock, color: "#2340b5" },
  { key: "active", label: "Active", icon: CircleDot, color: "#d97706" },
  { key: "completed", label: "Completed", icon: CheckCircle2, color: "#16a34a" },
  { key: "cancelled", label: "Cancelled", icon: XCircle, color: "#e4002b" },
];

const STATUS_STYLE = {
  upcoming: { label: "Upcoming", color: "#2340b5", bg: "#2340b514" },
  active: { label: "Active", color: "#d97706", bg: "#f59e0b1a" },
  completed: { label: "Completed", color: "#16a34a", bg: "#16a34a14" },
  cancelled: { label: "Cancelled", color: "#e4002b", bg: "#e4002b14" },
};

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("en-CA", { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-[#565b73]">{label}</p>
        <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ backgroundColor: `${color}14` }}><Icon className="h-4 w-4" style={{ color }} /></span>
      </div>
      <p className="mt-2 font-display text-2xl font-black text-[#12142b]">{value}</p>
    </div>
  );
}

function OccupancyBar({ booked, total }) {
  const pct = total > 0 ? Math.round((booked / total) * 100) : 0;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-[10px] text-[#565b73]"><span>{booked}/{total} seats</span><span>{pct}% full</span></div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#f4f6fb]"><div className="h-full rounded-full bg-[#2340b5]" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function RideCard({ ride, selected, onClick }) {
  const st = STATUS_STYLE[ride.tab] || STATUS_STYLE.upcoming;
  return (
    <button
      data-testid={`ride-card-${ride.id}`}
      onClick={onClick}
      className={`w-full rounded-2xl border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${selected ? "border-[#2340b5] ring-2 ring-[#2340b5]/25" : "border-[#12142b]/8"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 font-display text-base font-bold text-[#12142b]"><MapPin className="h-4 w-4 shrink-0 text-[#2340b5]" /> {ride.route}</p>
        <span className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: st.color, backgroundColor: st.bg }}>{st.label}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#565b73]">
        <span className="inline-flex items-center gap-1"><span className="grid h-5 w-5 place-items-center rounded-full bg-[#2340b5]/10 text-[9px] font-bold text-[#2340b5]">{initials(ride.driver)}</span>{ride.driver}</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDateTime(ride.depart_at)}</span>
        <span className="inline-flex items-center gap-1 font-semibold text-[#12142b]"><DollarSign className="h-3 w-3 text-[#565b73]" /> {money(ride.price)}/seat</span>
      </div>
      <OccupancyBar booked={ride.seats_booked} total={ride.seats} />
      <div className="mt-2 flex items-center justify-between border-t border-[#12142b]/6 pt-2">
        <span className="text-xs text-[#565b73]">Est. earnings <b className="text-[#16a34a]">{money(ride.price * ride.seats_booked)}</b></span>
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-[#2340b5]">Details <ChevronRight className="h-3.5 w-3.5" /></span>
      </div>
    </button>
  );
}

function Timeline({ ride }) {
  if (!ride) return null;
  const steps = [
    { label: "Ride created", at: ride.created_at, done: true },
    { label: "Scheduled departure", at: ride.depart_at, done: ride.tab !== "upcoming" },
  ];
  if (ride.tab === "completed") {
    steps.push({ label: "Trip started", at: ride.started_at || "", done: true });
    steps.push({ label: "Ride completed", at: ride.completed_at || ride.depart_at, done: true });
  } else if (ride.tab === "cancelled") {
    steps.push({ label: "Ride cancelled", at: ride.cancelled_at || "", done: true, bad: true });
  } else if (ride.tab === "active") {
    steps.push({ label: "Trip started", at: ride.started_at || "", done: !!ride.started_at });
  }
  return (
    <div data-testid="ride-timeline">
      <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-[#12142b]"><Route className="h-4 w-4 text-[#565b73]" /> Timeline</p>
      <div className="space-y-3">
        {steps.map((s, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${s.bad ? "bg-[#e4002b]" : s.done ? "bg-[#16a34a]" : "bg-[#c9cede]"}`}>
              {s.bad ? <XCircle className="h-3 w-3 text-white" /> : <CheckCircle2 className="h-3 w-3 text-white" />}
            </span>
            <div>
              <p className="text-sm font-medium text-[#12142b]">{s.label}</p>
              {s.at && <p className="text-xs text-[#565b73]">{fmtDateTime(s.at)}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RideDetail({ ride, detail, loading, onMessage, onCancel }) {
  if (!ride) return (
    <div className="grid h-full place-items-center rounded-2xl border border-dashed border-[#12142b]/12 bg-white p-10 text-center text-sm text-[#565b73]">
      Select a ride to see passengers, earnings and its timeline.
    </div>
  );
  const st = STATUS_STYLE[ride.tab] || STATUS_STYLE.upcoming;
  const e = detail?.earnings || {};
  const pax = detail?.passengers || [];
  const driver = detail?.driver || { name: ride.driver };
  const canCancel = ride.tab === "upcoming" || ride.tab === "active";
  return (
    <div className="space-y-4 rounded-2xl border border-[#12142b]/8 bg-white p-5 shadow-sm" data-testid="ride-detail-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-display text-lg font-black text-[#12142b]">{ride.route}</p>
          <p className="text-xs text-[#565b73]">Ride #{(ride.id || "").slice(0, 8)}</p>
        </div>
        <span className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: st.color, backgroundColor: st.bg }}>{st.label}</span>
      </div>

      {/* Driver */}
      <div className="flex items-center gap-3 rounded-xl bg-[#f4f6fb] p-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-[#2340b5]/10 text-sm font-bold text-[#2340b5]">{initials(driver.name)}</span>
        <div>
          <p className="text-sm font-semibold text-[#12142b]">{driver.name}</p>
          {driver.phone && <p className="flex items-center gap-1 text-xs text-[#565b73]"><Phone className="h-3 w-3" /> {driver.phone}</p>}
        </div>
      </div>

      {/* Ride info */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-[#f4f6fb] p-2.5"><p className="text-[10px] text-[#565b73]">Departs</p><p className="text-xs font-bold text-[#12142b]">{fmtDateTime(ride.depart_at)}</p></div>
        <div className="rounded-xl bg-[#f4f6fb] p-2.5"><p className="text-[10px] text-[#565b73]">Seats</p><p className="text-xs font-bold text-[#12142b]">{ride.seats_booked}/{ride.seats}</p></div>
        <div className="rounded-xl bg-[#f4f6fb] p-2.5"><p className="text-[10px] text-[#565b73]">Price/seat</p><p className="text-xs font-bold text-[#12142b]">{money(ride.price)}</p></div>
      </div>

      {loading && <p className="py-4 text-center text-sm text-[#565b73]"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></p>}

      {!loading && (
        <>
          {/* Earnings */}
          <div className="rounded-xl border border-[#16a34a]/20 bg-[#16a34a]/5 p-4" data-testid="ride-earnings">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#12142b]"><TrendingUp className="h-4 w-4 text-[#16a34a]" /> Earnings</p>
            {[["Collected from riders", e.collected], ["Base fares", e.base_fares], ["Fees & taxes", e.fees_taxes]].map(([l, v]) => (
              <div key={l} className="flex justify-between py-1 text-sm"><span className="text-[#565b73]">{l}</span><span className="font-semibold text-[#12142b]">{money(v)}</span></div>
            ))}
            <div className="mt-1 flex justify-between border-t border-[#16a34a]/20 pt-2 text-sm"><span className="font-bold text-[#16a34a]">Driver payout</span><span className="font-bold text-[#16a34a]">{money(e.driver_payout)}</span></div>
          </div>

          {/* Passengers */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#12142b]"><Users className="h-4 w-4 text-[#565b73]" /> Passengers ({pax.length})</p>
            {pax.length === 0 && <p className="rounded-xl bg-[#f4f6fb] py-4 text-center text-sm text-[#565b73]" data-testid="ride-passengers-empty">No passengers booked on this ride.</p>}
            <div className="space-y-2" data-testid="ride-passengers">
              {pax.map((p) => (
                <div key={p.booking_id} className="flex items-center justify-between gap-2 rounded-xl border border-[#12142b]/8 p-2.5">
                  <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-[#16a34a]/10 text-[10px] font-bold text-[#16a34a]"><UserRound className="h-4 w-4" /></span>
                    <div>
                      <p className="text-sm font-semibold text-[#12142b]">{p.name}</p>
                      <p className="text-xs text-[#565b73]">{p.seats} seat{p.seats === 1 ? "" : "s"} · {money(p.amount)}{p.kyc_status === "verified" ? " · ID verified" : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.status === "confirmed" ? "bg-[#16a34a]/12 text-[#16a34a]" : "bg-[#f59e0b]/15 text-[#d97706]"}`}>{p.status || "—"}</span>
                    <button
                      data-testid={`ride-message-passenger-${p.rider_id || p.booking_id}`}
                      disabled={!p.booking_id}
                      onClick={() => onMessage({ id: p.booking_id, route: ride.route, rider: p.name, driver: driver.name })}
                      className="grid h-7 w-7 place-items-center rounded-lg border border-[#2340b5]/25 text-[#2340b5] transition-colors hover:bg-[#2340b5]/8 disabled:opacity-40"
                      title={`View ${p.name} ↔ ${driver.name} conversation`}
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Timeline ride={ride} />

          {/* Admin actions */}
          {canCancel && (
            <button
              data-testid="ride-cancel-btn"
              onClick={() => onCancel(ride)}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#e4002b]/25 bg-[#e4002b]/5 px-4 py-2.5 text-sm font-bold text-[#e4002b] transition-colors hover:bg-[#e4002b]/10"
            >
              <Ban className="h-4 w-4" /> Cancel Ride
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function RideManagement() {
  const [tab, setTab] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [contact, setContact] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const load = useCallback(() => {
    setData(null);
    adminApi.get("/rides", { params: { status: tab, search } })
      .then((r) => { setData(r.data.items || []); setKpis(r.data.kpis); })
      .catch(() => { setData([]); setKpis(null); });
  }, [tab, search]);
  useEffect(() => { load(); }, [load]);

  // auto-select first ride of the current list
  useEffect(() => {
    if (data && data.length && (!selected || !data.find((r) => r.id === selected.id))) {
      setSelected(data[0]);
    }
    if (data && data.length === 0) setSelected(null);
  }, [data, selected]);

  // fetch detail for the selected ride
  useEffect(() => {
    if (!selected?.id) { setDetail(null); return; }
    setDetailLoading(true);
    setDetail(null);
    adminApi.get(`/rides/${selected.id}/detail`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail({ passengers: [], earnings: {} }))
      .finally(() => setDetailLoading(false));
  }, [selected]);

  const reloadDetail = useCallback(() => {
    if (!selected?.id) return;
    adminApi.get(`/rides/${selected.id}/detail`).then((r) => setDetail(r.data)).catch(() => {});
  }, [selected]);

  return (
    <div data-testid="ride-management-page">
      <PageHeader
        title="Ride Management"
        subtitle="Manage all rides, passengers and earnings across drivers."
        right={
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#565b73]" />
            <input
              data-testid="ride-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search route or driver…"
              className="w-64 rounded-xl border border-[#12142b]/12 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#2340b5]"
            />
          </div>
        }
      />

      <div className="space-y-5 p-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Kpi label="Upcoming" value={kpis ? kpis.upcoming : "—"} icon={CalendarClock} color="#2340b5" />
          <Kpi label="Active" value={kpis ? kpis.active : "—"} icon={CircleDot} color="#d97706" />
          <Kpi label="Completed" value={kpis ? kpis.completed : "—"} icon={CheckCircle2} color="#16a34a" />
          <Kpi label="Seats Sold" value={kpis ? kpis.seats_sold : "—"} icon={Users} color="#7c3aed" />
          <Kpi label="Est. Earnings" value={kpis ? money(kpis.est_earnings) : "—"} icon={DollarSign} color="#16a34a" />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="ride-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const count = kpis ? kpis[t.key] : null;
            return (
              <button
                key={t.key}
                data-testid={`ride-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${active ? "border-transparent bg-[#2340b5] text-white" : "border-[#12142b]/12 bg-white text-[#12142b] hover:bg-[#f4f6fb]"}`}
              >
                <t.icon className="h-4 w-4" /> {t.label}{count !== null ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="space-y-3 lg:col-span-2" data-testid="ride-list">
            {!data && <p className="py-10 text-center text-sm text-[#565b73]">Loading rides…</p>}
            {data && data.length === 0 && <p className="rounded-2xl border border-dashed border-[#12142b]/12 bg-white py-12 text-center text-sm text-[#565b73]">No {tab} rides.</p>}
            {data && data.map((r) => (
              <RideCard key={r.id} ride={r} selected={selected?.id === r.id} onClick={() => setSelected(r)} />
            ))}
          </div>
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6">
              <RideDetail ride={selected} detail={detail} loading={detailLoading} onMessage={setContact} onCancel={setCancelFor} />
            </div>
          </div>
        </div>
      </div>

      <TripMessagesDialog trip={contact} onClose={() => setContact(null)} />
      <CancelRideDialog
        ride={cancelFor}
        onClose={() => setCancelFor(null)}
        onDone={() => { setCancelFor(null); load(); reloadDetail(); }}
      />
    </div>
  );
}

function CancelRideDialog({ ride, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [refund, setRefund] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (ride) { setReason(""); setRefund(true); } }, [ride]);

  const confirm = async () => {
    setLoading(true);
    try {
      await adminApi.post(`/rides/${ride.id}/cancel`, { reason, refund });
      toast.success("Ride cancelled" + (refund ? " and passengers refunded" : ""));
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel ride");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!ride} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cancel-ride-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#e4002b]"><Ban className="h-5 w-5" /> Cancel Ride</DialogTitle>
          <p className="text-sm text-[#565b73]">{ride?.route} · {fmtDateTime(ride?.depart_at)}</p>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#12142b]">This cancels the ride for the driver and all booked passengers. This cannot be undone.</p>
          <div>
            <label className="mb-1 block text-sm font-semibold text-[#12142b]">Reason (optional)</label>
            <textarea
              data-testid="cancel-ride-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="e.g. Driver requested cancellation"
              className="w-full resize-none rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#e4002b]"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#12142b]">
            <input data-testid="cancel-ride-refund" type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} className="h-4 w-4 rounded border-[#12142b]/30 text-[#2340b5]" />
            Refund all passengers
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Keep Ride</button>
          <button data-testid="cancel-ride-confirm" disabled={loading} onClick={confirm} className="flex items-center gap-2 rounded-xl bg-[#e4002b] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#c00020] disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Cancel Ride
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
